import React, { useState, useEffect, useRef } from "react";
import { Car, Sun, PlugZap, } from "lucide-react";
import { Grid, Paper, Box, Typography, Divider, Card } from "@mui/material";
import EnergyFlow from "./EnergyFlow";
import StorageSystem from "./StorageSystem";
import DetailModal from "./DetailModal";
import ChargerApi from "app/views/charger/ChargerApi";
import VehicleGraphApi from "app/views/vehicle/VehicleGraphApi";
import DashboardApi from "app/views/dashboard/DashboardApi";
import electricBus from "./ev_charging.png";

const SolarPanelIcon = ({ size = 26, color = "#2563EB" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 64 64"
    fill="none"
    stroke={color}
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
   
    <polygon points="10,22 54,22 48,6 16,6" />
   
    <line x1="22" y1="6" x2="28" y2="22" />
    <line x1="34" y1="6" x2="38" y2="22" />
    <line x1="16" y1="14" x2="54" y2="14" />

  
    <line x1="32" y1="22" x2="32" y2="44" />
    <line x1="20" y1="44" x2="44" y2="44" />
    <line x1="20" y1="44" x2="18" y2="54" />
    <line x1="44" y1="44" x2="46" y2="54" />
  </svg>
);

export default function Dashboard() {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [modalState, setModalState] = useState({
    isOpen: false,
    type: "battery",
    data: null,
  });

  const [evChargers, setEvChargers] = useState([]);
  const [gridConsumption, setGridConsumption] = useState(0);
  const [batteryPower, setBatteryPower] = useState(0);
  const timezone = localStorage.getItem("orgTimezone");
  const selectedDepot = localStorage.getItem("selected_depot");
  const now = new Date().toLocaleTimeString("en-US", { hour12: false, timeZone: timezone });
  const hour = Number(now.split(":")[0]);
  const minute = Number(now.split(":")[1]);

const [solarProduction, setSolarProduction] = useState(0);

useEffect(() => {
  const updateSolar = () => {
    const now = new Date().toLocaleTimeString("en-US", { hour12: false, timeZone: timezone });
    const hour = Number(now.split(":")[0]);
    const minute = Number(now.split(":")[1]);
    const time = hour + minute / 60; 

    let solarValue = 0;

   if (selectedDepot == 200) {
    solarValue = 0;
} else {
    if (time >= 6 && time <= 16) {
        solarValue = 59 + Math.random(); // 59 ≤ value < 60
    } 
    else if (time > 16 && time <= 18) {
        const factor = (18 - time) / 2; 
        solarValue = (59 + Math.random()) * factor; 
    } 
    else {
        solarValue = 0;
    }
}

    setSolarProduction(Number(solarValue.toFixed(2)));
  };

  updateSolar();
  const interval = setInterval(updateSolar, 5000);
  return () => clearInterval(interval);
}, [timezone]);
const totalSolar = solarProduction;
const perInverter = selectedDepot == 200 ? 0 : solarProduction / 4;

  useEffect(() => {
  const api = new DashboardApi();

  const fetchGridPower = () => {
    api.getEnergyMeter().then(({ data }) => {
      if (data?.cpowerArray?.length > 0) {
        setGridConsumption(Number(data.cpowerArray[0].value) || 0);
      }
    });
  };

  fetchGridPower();
  const int = setInterval(fetchGridPower, 5000);
  return () => clearInterval(int);
}, []);

const load = gridConsumption; // live load from API
const nightSurplusRef = useRef(45); // persist remaining night surplus
useEffect(() => {
  if (selectedDepot == 200) return; // No BESS logic for depot 200

  const updateBessState = () => {
    setBatteries(prev => {
      const updated = prev.map(b => ({ ...b }));
      const now = new Date().toLocaleTimeString("en-US", { hour12: false, timeZone: timezone });
      const hour = Number(now.split(":")[0]);
      const surplus = batteryPower - load; 
      const maxPerBessKW = 15; 
      updated.forEach(b => {
        b.charge = Number((b.charge || 0).toFixed(2));
        if (b.prevCharge === undefined || b.prevCharge === null) {
          b.prevCharge = b.charge;
        }
        if (b.dischargeRate === undefined || b.dischargeRate === null) {
          b.dischargeRate = 0;
        }
        if (b.chargeRate === undefined || b.chargeRate === null) {
          b.chargeRate = 0;
        }
      });
     // 6 PM – 6 AM
      if (hour >= 18 || hour < 6) {
        const fixedSurplus = 45;
        const maxPerBessKW = 15;

        // no charging after 6 PM
        if (load === 0) {
          // no load - batteries remain idle
          updated.forEach(b => {
            b.chargeRate = 0;
            b.dischargeRate = 0;
          });
        } else {
          // load > 0 - discharge batteries one by one, not below 20%
          let remainingDemand = load;
          for (let i = 0; i < updated.length && remainingDemand > 0; i++) {
            const b = updated[i];
            if (b.charge <= 20) {
              b.prevCharge = b.charge;
              continue;
            }

            const currentKW = (b.charge / 100) * maxPerBessKW;
            const maxDeliverableKW = currentKW;
            const maxKWPerTick = 0.2; 
            const deliverKW = Math.min(remainingDemand, maxDeliverableKW, maxKWPerTick);

            if (deliverKW > 0) {
              const deltaPercent = (deliverKW / maxPerBessKW) * 100;
              let newCharge = Number((b.charge - deltaPercent).toFixed(2));
              if (newCharge < 20) newCharge = 20; // minimum limit

              const percentDelta = Number((b.prevCharge - newCharge).toFixed(2));
              const deltaKW = (percentDelta / 100) * maxPerBessKW;
              b.dischargeRate = Number(((b.dischargeRate || 0) + deltaKW).toFixed(2));
              b.charge = newCharge;
              b.chargeRate = 0;

              remainingDemand = Math.max(0, remainingDemand - deliverKW);
              break; // discharge one battery at a time
            }
          }
        }
      }

      // DAYTIME LOGIC (normal solar behavior)
      else if (surplus > 0 && totalSolar > 0) {
        let remainingSurplus = surplus;
        const maxPerBessKW = 15;

        for (let i = 0; i < updated.length && remainingSurplus > 0; i++) {
          const b = updated[i];
          if (b.charge >= 100) continue;

          const currentKW = (b.charge / 100) * maxPerBessKW;
          const neededToFullKW = Math.max(0, maxPerBessKW - currentKW);
          const maxKWPerTick = 0.2;
          const deliverKW = Math.min(remainingSurplus, neededToFullKW, maxKWPerTick);

          if (deliverKW > 0) {
            const deltaPercent = (deliverKW / maxPerBessKW) * 100;
            const newCharge = Math.min(100, Number((b.charge + deltaPercent).toFixed(2)));
            b.charge = newCharge;
            b.chargeRate = Number(((b.charge / 100) * maxPerBessKW).toFixed(2));
            b.dischargeRate = 0;
            remainingSurplus = Math.max(0, remainingSurplus - deliverKW);
            break;
          }
        }
      }

      // Evening discharging 
      else if (surplus < 0 && hour >= 16) {
        let remainingDemand = Math.abs(surplus);
        const maxPerBessKW = 15;

        for (let i = 0; i < updated.length && remainingDemand > 0; i++) {
          const b = updated[i];
          if (b.charge <= 20) {
            continue;
          }

          const currentKW = (b.charge / 100) * maxPerBessKW;
          const maxDeliverableKW = currentKW;
          const maxKWPerTick = 0.2;
          const deliverKW = Math.min(remainingDemand, maxDeliverableKW, maxKWPerTick);

          if (deliverKW > 0) {
            const deltaPercent = (deliverKW / maxPerBessKW) * 100;
            let newCharge = Number((b.charge - deltaPercent).toFixed(2));
            if (newCharge < 20) newCharge = 20;

            const percentDelta = Number((b.prevCharge - newCharge).toFixed(2));
            const deltaKW = (percentDelta / 100) * maxPerBessKW;
            b.dischargeRate = Number(((b.dischargeRate || 0) + deltaKW).toFixed(2));
            b.charge = newCharge;
            b.chargeRate = Number(((b.charge / 100) * maxPerBessKW).toFixed(2));

            remainingDemand = Math.max(0, remainingDemand - deliverKW);
            break;
          }
        }
      }
      else {
         updated.forEach(b => {
           b.chargeRate = 0;
          b.dischargeRate = 0;
        });
      }

      updated.forEach(b => {
        const maxPerBessKW = 15;
        const deltaPercent = b.charge - (b.prevCharge ?? b.charge);
        const deltaKW = (deltaPercent / 100) * maxPerBessKW;

        if (deltaKW > 0) {
          b.chargeRate = Number(deltaKW.toFixed(2));
          b.dischargeRate = 0;
        } else if (deltaKW < 0) {
          b.chargeRate = 0;
          b.dischargeRate = Number(Math.abs(deltaKW).toFixed(2));
        } else {
          b.chargeRate = 0;
          b.dischargeRate = 0;
        }
        b.prevCharge = b.charge;
      });

      return updated;
    });
  };
  

  updateBessState();

  const interval = setInterval(updateBessState, 5000);
  return () => clearInterval(interval);
}, [totalSolar, load, hour, selectedDepot, timezone]);

useEffect(() => {
  console.log("Battery power from EnergyFlow:", batteryPower);
}, [batteryPower]);


// force BESS to 0 for depot 200
useEffect(() => {
  if (selectedDepot == 200) {
    setBatteries(prev =>
      prev.map(b => ({ ...b, charge: 0, chargeRate: 0, dischargeRate: 0 }))
    );
  }
}, [selectedDepot]);


  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

 useEffect(() => {
  const chargerApi = new ChargerApi();
  const vehicleApi = new VehicleGraphApi();
  let isCancelled = false;

  const fetchData = async () => {
    try {
      const [chargerRes, vehicleRes] = await Promise.all([
        chargerApi.showChargerStMonitorReport(),
        vehicleApi.getVehicleChargingStatus(),
      ]);

      if (isCancelled) return;

      const vehicleDurations = vehicleRes.data || [];
      const formatted = (chargerRes.data || []).map((c, idx) => {
        const power = c.power ? parseFloat(Number(c.power).toFixed(2)) : 0.0;
        const connectorLabel = c.connector_id ? `C${c.connector_id}` : `C${idx + 1}`;

        const matchedVehicle = vehicleDurations.find(
          (v) =>
            v.charge_box_id === c.charge_box_id &&
            (v.connector_id === c.connector_id || v.charge_box_id.includes(c.charge_box_id))
        );

        const connectedTime = matchedVehicle?.duration || "-";

        return {
          id: idx + 1,
          name: `${c.charge_box_id || `Charger ${idx + 1}`} (${connectorLabel})`,
          status: c.status || "Unknown", 
          power,
          vehicleId: c.vehicle_license_plate || "-",
          vehicleSoC: c.Percent ?? 0,
          connectorId: connectorLabel,
          maxPower: 22,
          connectedTime, 
          voltage: c.V ? parseFloat(c.V).toFixed(2) : "—",
          current: c.A ? parseFloat(c.A).toFixed(2) : "—",
        };
      });

      setEvChargers(formatted);
    } catch (err) {
      console.error("Error fetching charger data:", err);
    }
  };

  fetchData();
  const interval = setInterval(fetchData, 10000);
  return () => {
    isCancelled = true;
    clearInterval(interval);
  };
}, []);

  const energyData = {
    solarProduction: 51.2,
    gridConsumption: 42.7,
    houseConsumption: 8.5,
    batteryLevel: 58,
    batteryFlow: 0,
  };


 const [batteries, setBatteries] = useState(() => {
  const saved = sessionStorage.getItem("bess_state");
  if (saved) {
    const parsed = JSON.parse(saved);
    return parsed.map(b => ({
      ...b,
      charge: Math.max(b.charge || 0, 20),
      chargeRate: b.chargeRate ?? 0,
      dischargeRate: b.dischargeRate ?? 0,
      capacity: b.capacity ?? 100,
    }));
  }
  return [
    { id: 1, name: "BESS 1", charge: 100, chargeRate: 0, dischargeRate: 0, capacity: 100 },
    { id: 2, name: "BESS 2", charge: 75, chargeRate: 0, dischargeRate: 0, capacity: 100 },
    { id: 3, name: "BESS 3", charge: 50, chargeRate: 0, dischargeRate: 0, capacity: 100 },
    { id: 4, name: "BESS 4", charge: 40, chargeRate: 0, dischargeRate: 0, capacity: 100 },
  ];
});


useEffect(() => {
  sessionStorage.setItem("bess_state", JSON.stringify(batteries));
}, [batteries]);


// update inverters power dynamically:
const inverters = [
  { id: 1, name: "PV Unit 1", power: parseFloat(perInverter.toFixed(2)),  status: perInverter > 0 ? "Generating" : "Not generating", efficiency: 98.5, temperature: 45, voltage: 380, current: 34.5 },
  { id: 2, name: "PV Unit 2", power: parseFloat(perInverter.toFixed(2)),  status: perInverter > 0 ? "Generating" : "Not generating", efficiency: 97.8, temperature: 46, voltage: 378, current: 34.7 },
  { id: 3, name: "PV Unit 3", power: parseFloat(perInverter.toFixed(2)),  status: perInverter > 0 ? "Generating" : "Not generating", efficiency: 98.2, temperature: 44, voltage: 382, current: 34.0 },
  { id: 4, name: "PV Unit 4", power: parseFloat(perInverter.toFixed(2)),  status: perInverter > 0 ? "Generating" : "Not generating", efficiency: 96.5, temperature: 48, voltage: 375, current: 6.4 },
];


  const consumptionItems = [
    { id: 1, name: "EV Charger 1", power: 0.1, status: "standby", voltage: 230, current: 0.4, sessionEnergy: 0, maxPower: 22, vehicleId: "BMW-X5-2024", vehicleSoC: 85, connectedTime: "2h 15m" },
    { id: 2, name: "EV Charger 2", power: 0, status: "idle", voltage: 230, current: 0, sessionEnergy: 0, maxPower: 22 },
    { id: 3, name: "EV Charger 3", power: 11.2, status: "charging", voltage: 230, current: 48.7, sessionEnergy: 24.8, maxPower: 22, vehicleId: "TESLA-M3-2023", vehicleSoC: 67, connectedTime: "1h 42m" },
    { id: 4, name: "EV Charger 4", power: 7.3, status: "charging", voltage: 230, current: 31.7, sessionEnergy: 15.2, maxPower: 22, vehicleId: "AUDI-E-TRON", vehicleSoC: 42, connectedTime: "45m" },
  ];

  const handleBatteryClick = (battery) => {
    setModalState({ isOpen: true, type: "battery", data: battery });
  };

  const handleInverterClick = (inverter) => {
    setModalState({ isOpen: true, type: "inverter", data: inverter });
  };

  const handleConsumptionClick = (item) => {
    setModalState({ isOpen: true, type: "consumption", data: item });
  };

  const closeModal = () => {
    setModalState({ isOpen: false, type: "battery", data: null });
  };

 const cardPaperSx = {
  p: 1,
  borderRadius: 3,
  boxShadow: 3,
  display: "flex",
  flexDirection: "column",
  gap: 2,
  background: "white",
  height: "100%",          
  maxHeight: "50vh",       
  overflow: "hidden",       
};
  return (
    <Box sx={{ width: "100%", py: 1, px: { xs: 1, sm: 2, md: 2.5 }, background: "transparent" }}>
      <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
            Microgrid Energy Management System
          </Typography>
           <Box sx={{ display: "flex", gap: 1, mt: 0.5, alignItems: "center", justifyContent: "flex-start" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "success.main", boxShadow: 2 }} />
        <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
        System Online
        </Typography>
        </Box>
        <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
        Status: Connected
        </Typography>
        </Box>
        </div>

      <div style={{ textAlign: "right" }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Last updated:
        </Typography>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          {currentTime.toLocaleTimeString("en-US", {
            timeZone: timezone,
            hour: "2-digit",
            minute: "2-digit",
            hour12: true, 
          })}
        </Typography>
      </Box>
    </div>


      </Box>

      <Grid container spacing={0.5} alignItems="stretch">
      <Grid item xs={12} md={6} lg={3}>
     <Box
      sx={{
        p: 1,
        borderRadius: 3,
        boxShadow: 3,
        background: "white",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        maxHeight: "50vh",
        overflow: "hidden",
      }}
    >
  
    <EnergyFlow solarProduction={solarProduction} onBatteryChange={setBatteryPower} />
</Box>


      </Grid>


        {/* Solar PV */}
        <Grid item xs={12} md={6} lg={3}>
          <Paper sx={cardPaperSx}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#F9FAFB",borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ p: 1, bgcolor: "#EFF6FF", borderRadius: 1 }}>
                <SolarPanelIcon size={18} className="text-blue-600" />
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
                Solar PV
              </Typography>
            </Box>

            <Typography variant="h6" sx={{ fontWeight: 700, color: "#2563EB", fontSize: "1rem" }}>
              {Number(solarProduction.toFixed(2))} kW
            </Typography>
          </Box>


            <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
              {inverters.map((inv) => (
                <Card key={inv.id} 
                sx={{ 
                  mb: 1.5, 
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "grey.100",
                  borderRadius: 2, 
                }}>
                <Box
                  key={inv.id}
                  onClick={() => handleInverterClick(inv)}
                  sx={{
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": { backgroundColor: "#fafafa" },
                    mb: 1,
                  }}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: inv.status === "active" ? "success.main" : "grey.400" }} />
                      <Typography variant="body2" color="text.secondary">{inv.name}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>{inv.power} kW</Typography>
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">Power Output</Typography>
                      <Typography variant="caption" color="text.secondary">{Math.round((inv.power / 15) * 100)}%</Typography>
                    </Box>
                    <Box sx={{ width: "100%", bgcolor: "grey.200", borderRadius: 1, height: 8 }}>
                      <Box sx={{ width: `${Math.min((inv.power / 15) * 100, 100)}%`, height: "100%", background: "linear-gradient(90deg,#60a5fa,#3b82f6)", borderRadius: 1 }} />
                    </Box>
                  </Box>
                </Box>
                </Card>
              ))}

            </Box>
          </Paper>
        </Grid>

        {/* Battery Energy Storage System */}
       <Grid item xs={12} md={6} lg={3}>
  <Box
    sx={{
      p: 1,
      borderRadius: 3,
      boxShadow: 3,
      background: "white",
      display: "flex",
      flexDirection: "column",
      height: "100%",
      maxHeight: "50vh",
      overflow: "hidden",
    }}
  >
    <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
      <StorageSystem batteries={batteries} onBatteryClick={handleBatteryClick} />
    </Box>
  </Box>
</Grid>



        {/* EV Charging */}
     <Grid item xs={12} md={6} lg={3}>
        <Paper
          sx={{
            ...cardPaperSx,
            height: "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", bgcolor: "#F9FAFB",borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
         
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ p: 1, bgcolor: "#FFFBEB", borderRadius: 1 }}>
              <img
                          src={electricBus}
                          width={18}
                          alt="electric bus"
                          style={{ filter: "invert(51%) sepia(94%) saturate(1200%) hue-rotate(2deg) brightness(95%) contrast(90%)" }}
                        />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "1rem" }}>
              EV Charging
            </Typography>
          </Box>

          
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#D97706", fontSize: "1rem" }}>
              {(evChargers.length > 0 ? evChargers : consumptionItems)
                .reduce((sum, it) => sum + (it.power || 0), 0)
                .toFixed(2)} kW
            </Typography>
          </Box>


          <Box sx={{ overflowY: "auto", flexGrow: 1 }}>
            {(evChargers.length > 0 ? evChargers : consumptionItems).map((item) => (
              <Box
                key={item.id}
                onClick={() => handleConsumptionClick(item)}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.100",
                  mb: 1.5,
                  "&:hover": { boxShadow: 2, backgroundColor: "#fafafa", cursor: "pointer" },
                }}
              >
                    {/* Header */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <PlugZap
                            size={16}
                            style={{ 
                              color:
                                item.status?.toLowerCase() === "charging"
                                  ? "#16a34a" 
                                  : item.status?.toLowerCase() === "available"
                                  ? "#0284c7" 
                                  : item.status?.toLowerCase() === "standby"
                                  ? "#d97706" 
                                  : "#9ca3af",
                            }}
                          />

                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {item.name}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {item.power.toFixed(2)} kW
                      </Typography>
                    </Box>

               
                    {item.vehicleId && item.vehicleId !== "-" && (
                      // <Typography variant="caption" color="primary" sx={{ ml: 3 }}>
                      //    {item.vehicleId} &nbsp;&nbsp; SoC: {item.vehicleSoC ?? 0}%
                      // </Typography>
                      <Typography 
                        variant="caption" 
                        color="primary" 
                        sx={{ ml: 3, fontWeight: 'bold', fontSize: '1rem' }}
                      >
                        {item.vehicleId}
                      </Typography>

                    )}

                    {/* SoC Bar */}
                    {item.vehicleSoC !== undefined && (
                      <>
                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            Vehicle Battery
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {item.vehicleSoC}%
                          </Typography>
                        </Box>
                        <Box sx={{ width: "100%", bgcolor: "grey.200", borderRadius: 1, height: 6, mb: 1 }}>
                          <Box
                            sx={{
                              width: `${item.vehicleSoC}%`,
                              height: "100%",
                              borderRadius: 1,
                              background: "linear-gradient(90deg,#60a5fa,#3b82f6)",
                            }}
                          />
                        </Box>
                      </>
                    )}

                    {/* Status */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color:
                            item.status?.toLowerCase() === "charging"
                              ? "success.main"
                              : item.status?.toLowerCase() === "available"
                              ? "info.main"
                              : "text.secondary",
                          fontWeight: 600,
                        }}
                      >
                        {item.status}
                      </Typography>
                    </Box>

                    {/* Connected */}
                    {/* <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Connected
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {item.connectedTime}
                      </Typography>
                    </Box> */}

                    {/* Power Level */}
                    {/* <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="caption" color="text.secondary">
                        Power Level
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        0%
                      </Typography>
                    </Box> */}

                   {/* Voltage & Current */}
                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 400 }}>
                        Voltage: {item.voltage} V
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 400 }}>
                        Current: {item.current} A
                      </Typography>
                    </Box>


                    {/* <Box sx={{ width: "100%", bgcolor: "grey.200", borderRadius: 1, height: 6 }}>
                      <Box
                        sx={{
                          width: `${Math.min((item.power / item.maxPower) * 100, 100)}%`,
                          height: "100%",
                          borderRadius: 1,
                          background: "#d1d5db",
                        }}
                      />
                    </Box> */}
                  </Box>
                ))}

          </Box>
        </Paper>
      </Grid>
      </Grid>

      <DetailModal isOpen={modalState.isOpen} onClose={closeModal} type={modalState.type} data={modalState.data} />
    </Box>
  );
}
